import { Box, Stack } from "@mui/material";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";

import { useActorTransactionsQuery } from "Actors/Api/Queries/useActorTransactionsQuery";
import { localizeDescription } from "Billing/Utils/localizeDescription";
import { getTransactionTypeOptions } from "Forms/Utils/EnumOptions";
import {
  EntryDirection,
  type TransactionListItemDto,
  type TransactionType,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { InvoiceDownloadIconButton } from "Invoices/Components/InvoiceDownloadIconButton";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlDropdown, type BlDropdownValueType } from "Shared/Inputs/BlDropdown";
import { Price } from "Shared/Price/Price";
import { DateFormat, formatDate } from "Shared/Utils/Format/date";

const PAGE_SIZE = 10;
const columnHelper = createColumnHelper<TransactionListItemDto>();

interface ActorTransactionsTabProps {
  actorID: string;
}

export const ActorTransactionsTab = ({
  actorID,
}: ActorTransactionsTabProps): React.ReactNode => {
  const { t } = useResource();
  const [pageIndex, setPageIndex] = useState(0);
  const [transactionTypeFilter, setTransactionTypeFilter] =
    useState<TransactionType | null>(null);

  const handleFilterChange = (value: BlDropdownValueType) => {
    setTransactionTypeFilter(value === "" ? null : (value as TransactionType));
    setPageIndex(0);
  };

  const { data: transactionsData, isLoading } = useActorTransactionsQuery(
    actorID,
    {
      offset: pageIndex * PAGE_SIZE,
      limit: PAGE_SIZE,
      transactionType: transactionTypeFilter,
    },
  );

  const transactions = transactionsData?.items ?? [];
  const totalCount = transactionsData?.totalCount ?? 0;

  const columns = useMemo(
    () => [
      columnHelper.accessor("effectiveAt", {
        header: t(Resources.Billing.Date),
        enableSorting: false,
        meta: { maxWidth: 120 },
        cell: (info) => (
          <Box>{formatDate(info.getValue(), DateFormat.DateTime)}</Box>
        ),
      }),
      columnHelper.accessor("transactionType", {
        header: t(Resources.Billing.Type),
        enableSorting: false,
        meta: { maxWidth: 75 },
        cell: (info) => {
          const type = info.getValue();
          const typeKey =
            type as keyof typeof Resources.Billing.TransactionTypes;
          const proxy = Resources.Billing.TransactionTypes[typeKey];
          const translated = proxy ? t(proxy) : type;
          return <Box>{translated}</Box>;
        },
      }),
      columnHelper.accessor("description", {
        header: t(Resources.Billing.Description),
        enableSorting: false,
        meta: { fillRow: true },
        cell: (info) => (
          <Box
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              wordBreak: "break-word",
            }}
          >
            {localizeDescription(info.getValue()) || "-"}
          </Box>
        ),
      }),
      columnHelper.accessor("amount", {
        header: t(Resources.Billing.Amount),
        enableSorting: false,
        meta: { maxWidth: 100 },
        cell: (info) => {
          const direction = info.row.original.direction;
          const isCredit = direction === EntryDirection.Credit;
          const prefix = isCredit ? "+" : "-";
          return (
            <Box
              sx={(theme) => ({
                color: isCredit
                  ? theme.palette.success.main
                  : theme.palette.error.main,
                fontWeight: 600,
              })}
            >
              {prefix}
              <Price amount={Math.abs(info.getValue())} />
            </Box>
          );
        },
      }),
      columnHelper.accessor("balanceAfter", {
        header: t(Resources.Billing.BalanceAfter),
        enableSorting: false,
        meta: { maxWidth: 100 },
        cell: (info) => (
          <Box>
            <Price amount={info.getValue()} />
          </Box>
        ),
      }),
      columnHelper.display({
        id: "invoice",
        header: "",
        cell: (info) => {
          const invoiceID = info.row.original.invoiceID;
          if (!invoiceID) return null;
          return <InvoiceDownloadIconButton invoiceID={invoiceID} />;
        },
      }),
    ],
    [t],
  );

  const table = useReactTable({
    data: transactions,
    columns,
    getRowId: (row) => row.walletTransactionID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: totalCount,
    state: { pagination: { pageIndex, pageSize: PAGE_SIZE } },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === "function"
          ? updater({ pageIndex, pageSize: PAGE_SIZE })
          : updater;
      setPageIndex(next.pageIndex);
    },
  });

  return (
    <Box>
      <Stack direction="row" spacing={Spacing.M} sx={{ mb: Spacing.M }}>
        <BlDropdown
          name="transactionTypeFilter"
          options={getTransactionTypeOptions()}
          value={transactionTypeFilter ?? ""}
          onChange={handleFilterChange}
          size="small"
          sx={{ minWidth: 200 }}
          showEmptyOptionLabel
        />
      </Stack>
      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        totalCount={totalCount}
        isPaginationDisabled={totalCount <= PAGE_SIZE}
      />
    </Box>
  );
};
